import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import { GridColDef } from "@mui/x-data-grid";

import { LojaPageContainer, LojaDataGrid } from "layouts/pages/loja/common";
import ConfirmDialog from "components/ConfirmDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController, setMessage } from "context";
import { IServicoOrcamento } from "interfaces/orcamento";
import { formatMoneyBR } from "helpers/formatMoney";
import {
  atualizarServicoOrcamento,
  criarServicoOrcamento,
  deletarServicoOrcamento,
  listarServicosOrcamentoAdmin,
  ServicoOrcamentoPayload,
} from "services/orcamentoServicos";
import ServicoOrcamentoModal from "./components/ServicoOrcamentoModal";
import { ORCAMENTO_ACCENT } from "./components/orcamento-servicos.theme";

const emptyForm: ServicoOrcamentoPayload = {
  nome: "",
  descricao: "",
  ordem: 0,
  ativo: true,
  exibirSite: true,
  precoUnitario: 0,
  permiteQuantidade: true,
  unidadeMedida: "un",
};

type FiltroVisibilidade = "" | "site" | "ativos" | "inativos";

const FILTRO_OPTIONS: { value: FiltroVisibilidade; label: string }[] = [
  { value: "", label: "Todos os serviços" },
  { value: "site", label: "Visíveis no site" },
  { value: "ativos", label: "Somente ativos" },
  { value: "inativos", label: "Inativos ou ocultos" },
];

function StatMini({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        color={accent ? ORCAMENTO_ACCENT : "text.primary"}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function OrcamentoServicos() {
  const [, dispatch] = useMaterialUIController();
  const [rows, setRows] = useState<IServicoOrcamento[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState<FiltroVisibilidade>("");
  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState<IServicoOrcamento | null>(null);
  const [form, setForm] = useState<ServicoOrcamentoPayload>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState<{ open: boolean; id?: number; nome?: string }>({
    open: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarServicosOrcamentoAdmin();
      setRows(data);
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: error instanceof Error ? error.message : "Falha ao carregar serviços.",
      });
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    switch (filtro) {
      case "site":
        return rows.filter((r) => r.ativo && r.exibirSite);
      case "ativos":
        return rows.filter((r) => r.ativo);
      case "inativos":
        return rows.filter((r) => !r.ativo || !r.exibirSite);
      default:
        return rows;
    }
  }, [rows, filtro]);

  const stats = useMemo(() => {
    const noSite = rows.filter((r) => r.ativo && r.exibirSite).length;
    const inativos = rows.filter((r) => !r.ativo).length;
    const comPreco = rows.filter((r) => Number(r.precoUnitario) > 0);
    const precoMedio =
      comPreco.length > 0
        ? comPreco.reduce((s, r) => s + Number(r.precoUnitario), 0) / comPreco.length
        : 0;
    return { total: rows.length, noSite, inativos, precoMedio };
  }, [rows]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpenModal(true);
  };

  const openEdit = (servico: IServicoOrcamento) => {
    setEditing(servico);
    setForm({
      nome: servico.nome,
      descricao: servico.descricao ?? "",
      ordem: servico.ordem,
      ativo: servico.ativo,
      exibirSite: servico.exibirSite,
      precoUnitario: Number(servico.precoUnitario),
      permiteQuantidade: servico.permiteQuantidade,
      unidadeMedida: servico.unidadeMedida,
    });
    setOpenModal(true);
  };

  const handleFormChange = (patch: Partial<ServicoOrcamentoPayload>) => {
    setForm((f) => ({ ...f, ...patch }));
  };

  const handleSave = async () => {
    if (!form.nome?.trim()) {
      setMessage(dispatch, { open: true, severity: "error", text: "Informe o nome do serviço." });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        nome: form.nome.trim(),
        descricao: form.descricao?.trim() || null,
        precoUnitario: Number(form.precoUnitario) || 0,
      };
      if (editing) {
        await atualizarServicoOrcamento(editing.id, payload);
        setMessage(dispatch, { open: true, severity: "success", text: "Serviço atualizado." });
      } else {
        await criarServicoOrcamento(payload);
        setMessage(dispatch, { open: true, severity: "success", text: "Serviço criado." });
      }
      setOpenModal(false);
      load();
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: error instanceof Error ? error.message : "Falha ao salvar.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (confirm.id == null) return;
    try {
      await deletarServicoOrcamento(confirm.id);
      setMessage(dispatch, { open: true, severity: "success", text: "Serviço removido." });
      setConfirm({ open: false });
      load();
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: error instanceof Error ? error.message : "Falha ao excluir.",
      });
    }
  };

  const columns: GridColDef<IServicoOrcamento>[] = useMemo(
    () => [
      { field: "ordem", headerName: "Ordem", width: 72, align: "center", headerAlign: "center" },
      { field: "nome", headerName: "Serviço", flex: 1, minWidth: 180 },
      {
        field: "precoUnitario",
        headerName: "Preço unit.",
        width: 120,
        valueFormatter: (params) => formatMoneyBR(params.value),
      },
      { field: "unidadeMedida", headerName: "Unidade", width: 88 },
      {
        field: "permiteQuantidade",
        headerName: "Qtd.",
        width: 72,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value ? "Sim" : "Não"}
            variant="outlined"
            color={params.value ? "success" : "default"}
          />
        ),
      },
      {
        field: "exibirSite",
        headerName: "Site",
        width: 88,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value ? "Sim" : "Não"}
            color={params.value ? "info" : "default"}
            variant={params.value ? "filled" : "outlined"}
          />
        ),
      },
      {
        field: "ativo",
        headerName: "Status",
        width: 96,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value ? "Ativo" : "Inativo"}
            color={params.value ? "success" : "default"}
          />
        ),
      },
      {
        field: "actions",
        headerName: "Ações",
        width: 100,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <MDBox display="flex" alignItems="center" justifyContent="center" gap={0.25}>
            <Tooltip title="Editar">
              <IconButton size="small" color="inherit" onClick={() => openEdit(params.row)}>
                <Icon fontSize="small">edit</Icon>
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir">
              <IconButton
                size="small"
                color="error"
                onClick={() =>
                  setConfirm({ open: true, id: params.row.id, nome: params.row.nome })
                }
              >
                <Icon fontSize="small">delete</Icon>
              </IconButton>
            </Tooltip>
          </MDBox>
        ),
      },
    ],
    [],
  );

  return (
    <LojaPageContainer
      title="Orçamento · Serviços"
      subtitle="Catálogo de serviços da landing page e base de precificação das solicitações."
      actions={
        <MDButton
          variant="gradient"
          color="dark"
          onClick={openCreate}
          sx={{
            background: `linear-gradient(195deg, ${ORCAMENTO_ACCENT}, #e04a1a)`,
          }}
        >
          <Icon sx={{ mr: 0.5 }}>add</Icon>
          Novo serviço
        </MDButton>
      }
    >
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatMini label="Serviços cadastrados" value={stats.total} accent />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Visíveis no site" value={stats.noSite} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Inativos" value={stats.inativos} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Preço médio unit." value={formatMoneyBR(stats.precoMedio)} />
        </Grid>
      </Grid>

      <MDBox
        mb={2}
        p={2}
        borderRadius="lg"
        sx={{
          bgcolor: "grey.50",
          border: "1px solid",
          borderColor: "grey.200",
          borderLeft: `4px solid ${ORCAMENTO_ACCENT}`,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <RequestQuoteOutlinedIcon sx={{ color: ORCAMENTO_ACCENT }} />
            <MDTypography variant="button" fontWeight="medium" color="text">
              Catálogo de serviços
            </MDTypography>
          </Stack>
          <Autocomplete
            options={FILTRO_OPTIONS}
            value={FILTRO_OPTIONS.find((o) => o.value === filtro) ?? FILTRO_OPTIONS[0]}
            getOptionLabel={(o) => o.label}
            onChange={(_, v) => setFiltro(v?.value ?? "")}
            sx={{ minWidth: { xs: "100%", sm: 240 } }}
            size="small"
            disableClearable
            renderInput={(params) => <TextField {...params} label="Filtrar" />}
          />
        </Stack>
      </MDBox>

      {!loading && filteredRows.length === 0 ? (
        <MDBox
          py={6}
          textAlign="center"
          sx={{
            border: "1px dashed",
            borderColor: "grey.300",
            borderRadius: 2,
            bgcolor: "grey.50",
          }}
        >
          <RequestQuoteOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
            {rows.length === 0 ? "Nenhum serviço cadastrado" : "Nenhum serviço neste filtro"}
          </MDTypography>
          <MDTypography variant="body2" color="text" mb={2}>
            {rows.length === 0
              ? "Cadastre os serviços oferecidos para montar o formulário de orçamento no site."
              : "Ajuste o filtro ou cadastre um novo serviço."}
          </MDTypography>
          {rows.length === 0 && (
            <MDButton
              variant="gradient"
              color="dark"
              onClick={openCreate}
              sx={{
                background: `linear-gradient(195deg, ${ORCAMENTO_ACCENT}, #e04a1a)`,
              }}
            >
              Criar primeiro serviço
            </MDButton>
          )}
        </MDBox>
      ) : (
        <LojaDataGrid
          rows={filteredRows}
          getRowId={(r) => r.id}
          columns={columns}
          loading={loading}
          initialState={{ pagination: { paginationModel: { pageSize: 15 } } }}
          sx={{
            "& .MuiDataGrid-cell[data-field='actions']": {
              overflow: "visible",
              justifyContent: "center",
            },
          }}
        />
      )}

      <ServicoOrcamentoModal
        open={openModal}
        editing={editing}
        form={form}
        saving={saving}
        onClose={() => setOpenModal(false)}
        onSave={handleSave}
        onChange={handleFormChange}
      />

      <ConfirmDialog
        open={confirm.open}
        title="Excluir serviço"
        message={`Excluir o serviço "${confirm.nome ?? ""}"? Se estiver em uso, será desativado.`}
        onClose={() => setConfirm({ open: false })}
        onConfirm={handleDelete}
      />
    </LojaPageContainer>
  );
}
